// src/components/ColaboradorDoMes/CriarColaboradorDoMes.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebaseConfig';
import { collection, getDocs, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUsers, FaCog, FaCheck, FaBuilding, FaStore, FaUserShield } from 'react-icons/fa';
import '../../styles/CriarColaboradorDoMes.css'; // Usaremos o mesmo CSS anterior, com pequenos acréscimos se precisar

const CriarColaboradorDoMes = ({ userData }) => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    
    // Filtros Dinâmicos
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [candidates, setCandidates] = useState([]);

    // Regras de Votação
    const [voterRule, setVoterRule] = useState({ value: 'loja', label: 'Apenas colaboradores da mesma Loja' });
    const [isSaving, setIsSaving] = useState(false);

    // Carregar todos os usuários e extrair Lojas/Deptos únicos
    useEffect(() => {
        const fetchUsers = async () => {
            const q = query(collection(db, 'users'), where('empresa_id', '==', userData.empresa_id));
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({ 
                id: d.id, 
                value: d.id, 
                label: d.data().name, 
                photoURL: d.data().photoURL,
                departamento: d.data().departamento || 'Sem Departamento',
                loja: d.data().loja || 'Sem Loja'
            })).filter(u => u.label); // Garante que tem nome
            setAllUsers(list);
        };
        fetchUsers();
    }, [userData]);

    const uniqueStores = useMemo(() => {
        const stores = [...new Set(allUsers.map(u => u.loja))];
        return stores.map(s => ({ value: s, label: s }));
    }, [allUsers]);

    const uniqueDepts = useMemo(() => {
        const depts = [...new Set(allUsers.map(u => u.departamento))];
        return depts.map(d => ({ value: d, label: d }));
    }, [allUsers]);

    // Atualiza os candidatos automaticamente quando o RH muda os filtros de Loja/Depto
    useEffect(() => {
        let filtered = allUsers;
        if (selectedStore && selectedStore.value !== 'todas') {
            filtered = filtered.filter(u => u.loja === selectedStore.value);
        }
        if (selectedDept && selectedDept.value !== 'todos') {
            filtered = filtered.filter(u => u.departamento === selectedDept.value);
        }
        // Pré-seleciona os filtrados, mas permite ao RH remover/adicionar manualmente
        setCandidates(filtered);
    }, [selectedStore, selectedDept, allUsers]);

    const voterRulesOptions = [
        { value: 'global', label: 'Todos os colaboradores da empresa' },
        { value: 'loja', label: 'Apenas colaboradores da mesma Loja dos candidatos' },
        { value: 'departamento', label: 'Apenas colaboradores do mesmo Departamento' }
    ];

    // Sanitiza objeto removendo campos undefined (Firestore não aceita undefined)
    const sanitize = (obj) =>
        Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ?? null]));

    const handleSave = async () => {
        if (!title || candidates.length === 0) {
            alert("Preencha o título e garanta que há candidatos selecionados.");
            return;
        }
        setIsSaving(true);
        try {
            const sanitizedCandidates = candidates.map(c => sanitize({
                id: c.id ?? null,
                name: c.label || c.name || null,
                photoURL: c.photoURL || null,
                loja: c.loja || null,
                departamento: c.departamento || null,
            }));

            await addDoc(collection(db, 'Empresas', userData.empresa_id, 'employeeOfMonth'), {
                title,
                targetStore: selectedStore?.value || 'Todas',
                targetDept: selectedDept?.value || 'Todos',
                scope: selectedStore ? 'loja' : selectedDept ? 'departamento' : 'global',
                scopeValue: selectedStore?.value || selectedDept?.value || 'Geral',
                candidates: sanitizedCandidates,
                voterRule: voterRule.value,
                status: 'active',
                createdAt: serverTimestamp(),
                createdBy: userData?.uid || userData?.id || null,
                month: new Date().toLocaleString('pt-BR', { month: 'long' }),
                year: new Date().getFullYear(),
                votedUsers: [],
            });
            navigate('/colaborador-do-mes');
        } catch (error) {
            console.error(error);
            alert("Erro ao criar votação.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="criar-colaborador-container">
            <button className="btn-back-flat" onClick={() => navigate(-1)}><FaArrowLeft /> Voltar</button>
            
            <div className="creation-card">
                <header>
                    <h2><FaCog /> Configurar Nova Eleição</h2>
                    <p>Defina o escopo usando a estrutura do seu organograma.</p>
                </header>

                <div className="form-premium">
                    <div className="input-group full-width">
                        <label>Título da Campanha</label>
                        <input type="text" placeholder="Ex: Destaques do Mês - Loja Matriz" value={title} onChange={e => setTitle(e.target.value)} />
                    </div>

                    <h4 className="section-divider"><FaStore /> 1. Quem são os Candidatos?</h4>
                    <p className="helper-text">Filtre por loja ou departamento. A lista de candidatos será preenchida automaticamente, mas você pode editá-la.</p>
                    
                    <div className="input-row">
                        <div className="input-group">
                            <label>Filtrar por Loja</label>
                            <Select 
                                options={[{value: 'todas', label: 'Todas as Lojas'}, ...uniqueStores]}
                                placeholder="Selecione a Loja..."
                                isClearable
                                onChange={setSelectedStore}
                            />
                        </div>
                        <div className="input-group">
                            <label>Filtrar por Departamento</label>
                            <Select 
                                options={[{value: 'todos', label: 'Todos os Departamentos'}, ...uniqueDepts]}
                                placeholder="Selecione o Departamento..."
                                isClearable
                                onChange={setSelectedDept}
                            />
                        </div>
                    </div>

                    <div className="input-group full-width">
                        <label><FaUsers /> Lista Final de Candidatos (Pode adicionar ou remover)</label>
                        <Select 
                            isMulti
                            options={allUsers}
                            value={candidates}
                            onChange={setCandidates}
                            placeholder="Adicione ou remova candidatos específicos..."
                            className="multi-select-premium"
                        />
                    </div>

                    <h4 className="section-divider" style={{marginTop: '30px'}}><FaUserShield /> 2. Regras de Votação (Quem pode votar?)</h4>
                    <div className="input-group full-width">
                        <Select 
                            options={voterRulesOptions}
                            value={voterRule}
                            onChange={setVoterRule}
                            isSearchable={false}
                        />
                        <small className="helper-text-bottom">
                            {voterRule.value === 'loja' && "O sistema bloqueará votos de pessoas que trabalham em lojas diferentes dos candidatos."}
                            {voterRule.value === 'departamento' && "Apenas colegas do mesmo departamento poderão acessar esta votação."}
                        </small>
                    </div>

                    <div className="creation-footer">
                        <button className="btn-save-survey" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Lançando...' : <><FaCheck /> Lançar Votação</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CriarColaboradorDoMes;